import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Shield,
  RefreshCw,
  Plus,
  Search,
  Eye,
  EyeOff,
  Edit,
  Trash2,
  Lock,
  Unlock,
  Tag,
  CheckCircle2,
  AlertCircle
} from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { superAdminAPI } from '../../utils/api';
import { toast } from 'react-toastify';
import useConfirm from '../../hooks/useConfirm';
import ConfirmationModal from '../../components/modals/ConfirmationModal';

const badgeByStatus = (hasValue) =>
  hasValue
    ? 'inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-emerald-50 text-emerald-700 border border-emerald-100'
    : 'inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-amber-50 text-amber-700 border border-amber-100';

const SuperAdminEnvPage = () => {
  const { t } = useLanguage();
  const { confirm, confirmProps } = useConfirm();

  const [variables, setVariables] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState('create');
  const [form, setForm] = useState({ key: '', value: '', is_secret: false, category: '', description: '' });
  const [showValue, setShowValue] = useState(false);
  const [saving, setSaving] = useState(false);

  const categoryLabels = useMemo(
    () => ({
      urls: t('superadmin.env.categories.urls'),
      core: t('superadmin.env.categories.core'),
      storage: t('superadmin.env.categories.storage'),
      email: t('superadmin.env.categories.email'),
      ai: t('superadmin.env.categories.ai'),
      video: t('superadmin.env.categories.video'),
      other: t('superadmin.env.categories.other')
    }),
    [t]
  );

  const loadVariables = useCallback(async (showToast = false) => {
    try {
      const response = await superAdminAPI.getEnvVars();
      setVariables(response.data.variables || []);
      if (showToast) {
        toast.success(t('superadmin.env.success.refreshed'));
      }
    } catch (error) {
      console.error('Error loading env variables:', error);
      toast.error(t('superadmin.env.errors.load'));
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [t]);

  useEffect(() => {
    loadVariables();
  }, [loadVariables]);

  const filteredVariables = useMemo(() => {
    const query = search.trim().toLowerCase();
    return variables.filter((variable) => {
      const matchesSearch = !query ||
        variable.key.toLowerCase().includes(query) ||
        (variable.description || '').toLowerCase().includes(query);
      const matchesCategory = categoryFilter === 'all' || variable.category === categoryFilter;
      return matchesSearch && matchesCategory;
    });
  }, [variables, search, categoryFilter]);

  const groupedVariables = useMemo(() => {
    return filteredVariables.reduce((acc, variable) => {
      const category = variable.category || 'other';
      if (!acc[category]) {
        acc[category] = [];
      }
      acc[category].push(variable);
      return acc;
    }, {});
  }, [filteredVariables]);

  const allKeys = useMemo(() => variables.map((item) => item.key).sort(), [variables]);

  const stats = useMemo(() => {
    const total = variables.length;
    const configured = variables.filter((item) => item.has_value).length;
    const missing = total - configured;
    const secret = variables.filter((item) => item.is_secret).length;

    return { total, configured, missing, secret };
  }, [variables]);

  const openCreateModal = () => {
    setModalMode('create');
    setForm({ key: '', value: '', is_secret: false, category: '', description: '' });
    setShowValue(false);
    setModalOpen(true);
  };

  const openEditModal = async (variable) => {
    try {
      setModalMode('edit');
      setShowValue(false);
      const response = await superAdminAPI.getEnvVar(variable.key);
      const data = response.data;
      setForm({
        key: data.key,
        value: data.value || '',
        is_secret: data.is_secret,
        category: data.category,
        description: data.description || ''
      });
      setModalOpen(true);
    } catch (error) {
      console.error('Error loading env variable:', error);
      toast.error(t('superadmin.env.errors.loadSingle'));
    }
  };

  const closeModal = () => {
    if (saving) return;
    setModalOpen(false);
  };

  const handleSelectKey = (key) => {
    const meta = variables.find((item) => item.key === key);
    setForm({
      key,
      value: '',
      is_secret: meta?.is_secret || false,
      category: meta?.category || '',
      description: meta?.description || ''
    });
  };

  const handleSave = async () => {
    if (!form.key || !form.value) {
      toast.warning(t('superadmin.env.errors.missing'));
      return;
    }

    setSaving(true);
    try {
      if (modalMode === 'edit') {
        await superAdminAPI.updateEnvVar(form.key, { value: form.value });
        toast.success(t('superadmin.env.success.updated'));
      } else {
        await superAdminAPI.createEnvVar({ key: form.key, value: form.value });
        toast.success(t('superadmin.env.success.saved'));
      }
      setModalOpen(false);
      await loadVariables();
    } catch (error) {
      console.error('Error saving env variable:', error);
      const message = error.response?.data?.error || t('superadmin.env.errors.save');
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (variable) => {
    const confirmed = await confirm({
      title: t('superadmin.env.confirmTitle'),
      message: t('superadmin.env.confirmDelete'),
      type: 'danger',
      confirmText: t('common.delete'),
      cancelText: t('common.cancel')
    });

    if (!confirmed) return;

    try {
      await superAdminAPI.deleteEnvVar(variable.key);
      toast.success(t('superadmin.env.success.deleted'));
      await loadVariables();
    } catch (error) {
      console.error('Error deleting env variable:', error);
      toast.error(t('superadmin.env.errors.delete'));
    }
  };

  const renderModal = () => (
    <AnimatePresence>
      {modalOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
        >
          <motion.div
            initial={{ scale: 0.98, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.98, opacity: 0 }}
            className="w-full max-w-xl rounded-lg bg-white border border-gray-200 shadow-xl"
          >
            <div className="border-b border-gray-100 px-6 py-4">
              <h3 className="text-lg font-semibold text-gray-900">
                {modalMode === 'create'
                  ? t('superadmin.env.modal.create')
                  : t('superadmin.env.modal.edit')}
              </h3>
              <p className="text-sm text-gray-500 mt-1">
                {form.description || t('superadmin.env.modal.helper')}
              </p>
            </div>

            <div className="px-6 py-4 space-y-4">
              <div>
                <label htmlFor="env-key" className="block text-sm font-medium text-gray-700 mb-2">
                  {t('superadmin.env.fields.key')}
                </label>
                {modalMode === 'create' ? (
                  <select
                    id="env-key"
                    value={form.key}
                    onChange={(e) => handleSelectKey(e.target.value)}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
                  >
                    <option value="">{t('superadmin.env.fields.selectKey')}</option>
                    {allKeys.map((key) => (
                      <option key={key} value={key}>{key}</option>
                    ))}
                  </select>
                ) : (
                  <input
                    id="env-key"
                    type="text"
                    value={form.key}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm bg-gray-50"
                    disabled
                  />
                )}
              </div>

              <div>
                <label htmlFor="env-value" className="block text-sm font-medium text-gray-700 mb-2">
                  {t('superadmin.env.fields.value')}
                </label>
                <div className="relative">
                  <input
                    id="env-value"
                    type={form.is_secret && !showValue ? 'password' : 'text'}
                    value={form.value}
                    onChange={(e) => setForm((prev) => ({ ...prev, value: e.target.value }))}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm pr-12"
                    aria-label={t('superadmin.env.fields.value')}
                  />
                  {form.is_secret && (
                    <button
                      type="button"
                      onClick={() => setShowValue((prev) => !prev)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      aria-label={showValue ? t('superadmin.env.actions.hideValue') : t('superadmin.env.actions.showValue')}
                    >
                      {showValue ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  )}
                </div>
                <div className="mt-2 inline-flex items-center text-xs text-gray-500">
                  {form.is_secret ? (
                    <span className="inline-flex items-center gap-1">
                      <Lock className="h-3 w-3" />
                      {t('superadmin.env.labels.secret')}
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1">
                      <Unlock className="h-3 w-3" />
                      {t('superadmin.env.labels.plain')}
                    </span>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('superadmin.env.fields.category')}
                </label>
                <div className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm bg-gray-50 flex items-center gap-2">
                  <Tag className="h-4 w-4 text-gray-400" />
                  <span>{categoryLabels[form.category] || form.category || t('superadmin.env.categories.other')}</span>
                </div>
              </div>
            </div>

            <div className="flex items-center justify-end gap-3 border-t border-gray-100 px-6 py-4">
              <button
                type="button"
                onClick={closeModal}
                className="text-sm px-4 py-2 rounded-lg border border-gray-200 text-gray-700 hover:border-gray-300"
                disabled={saving}
              >
                {t('common.cancel')}
              </button>
              <button
                type="button"
                onClick={handleSave}
                className="text-sm px-4 py-2 rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-60"
                disabled={saving || !form.key || !form.value}
              >
                {saving ? t('common.saving') : t('common.save')}
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );

  if (loading) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center h-[70vh]">
          <div className="flex items-center gap-3 text-gray-500">
            <RefreshCw className="w-5 h-5 animate-spin" />
            <span>{t('common.loading', 'Loading...')}</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex flex-col gap-2">
        <h1 className="text-2xl font-semibold text-gray-900 flex items-center gap-2">
          <Shield className="w-6 h-6 text-indigo-600" />
          {t('superadmin.env.title')}
        </h1>
        <p className="text-sm text-gray-600">{t('superadmin.env.subtitle')}</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="text-xs text-gray-500">{t('superadmin.env.stats.total', 'Total variables')}</div>
          <div className="text-2xl font-semibold text-gray-900 mt-1">{stats.total}</div>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="text-xs text-gray-500">{t('superadmin.env.stats.configured', 'Configured')}</div>
          <div className="text-2xl font-semibold text-emerald-600 mt-1">{stats.configured}</div>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="text-xs text-gray-500">{t('superadmin.env.stats.missing', 'Missing')}</div>
          <div className="text-2xl font-semibold text-amber-600 mt-1">{stats.missing}</div>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="text-xs text-gray-500">{t('superadmin.env.stats.secret', 'Secret keys')}</div>
          <div className="text-2xl font-semibold text-gray-900 mt-1">{stats.secret}</div>
        </div>
      </div>

      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <div className="flex flex-col lg:flex-row gap-4 lg:items-end lg:justify-between">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 flex-1">
            <div>
              <label htmlFor="env-search" className="block text-xs font-medium text-gray-600 mb-2">
                {t('superadmin.env.search')}
              </label>
              <div className="relative">
                <Search className="h-4 w-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  id="env-search"
                  type="text"
                  className="w-full border border-gray-200 rounded-lg px-9 py-2 text-sm"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder={t('superadmin.env.searchPlaceholder')}
                />
              </div>
            </div>
            <div>
              <label htmlFor="env-category" className="block text-xs font-medium text-gray-600 mb-2">
                {t('superadmin.env.fields.category')}
              </label>
              <select
                id="env-category"
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
              >
                <option value="all">{t('common.all')}</option>
                {Object.keys(categoryLabels).map((key) => (
                  <option key={key} value={key}>{categoryLabels[key]}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => {
                setRefreshing(true);
                loadVariables(true);
              }}
              className="text-xs px-3 py-2 rounded-lg border border-gray-200 text-gray-600 hover:border-indigo-200 inline-flex items-center gap-1.5"
              disabled={refreshing}
            >
              <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
              {t('common.refresh')}
            </button>
            <button
              type="button"
              onClick={openCreateModal}
              className="text-xs px-3 py-2 rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 inline-flex items-center gap-1.5"
            >
              <Plus className="h-4 w-4" />
              {t('superadmin.env.actions.add')}
            </button>
          </div>
        </div>
      </div>

      {filteredVariables.length === 0 ? (
        <div className="bg-white rounded-lg border border-gray-200 p-10 text-center text-gray-500 text-sm">
          {t('superadmin.env.empty', 'No variables found for this filter')}
        </div>
      ) : (
        <div className="space-y-4">
          {Object.entries(groupedVariables).map(([category, items]) => {
            const publicCount = items.filter((item) => !item.is_secret).length;
            const secretCount = items.filter((item) => item.is_secret).length;

            return (
              <div key={category} className="bg-white rounded-lg border border-gray-200">
                <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
                  <div>
                    <h2 className="text-sm font-semibold text-gray-900">
                      {categoryLabels[category] || category}
                    </h2>
                    <p className="text-xs text-gray-500 mt-0.5">
                      {t('superadmin.env.groupSummary')
                        .replace('{{public}}', publicCount)
                        .replace('{{secret}}', secretCount)}
                    </p>
                  </div>
                </div>

                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-100 text-sm">
                    <thead className="bg-gray-50 text-left">
                      <tr>
                        <th className="px-4 py-2.5 font-medium text-gray-500">{t('superadmin.env.table.key')}</th>
                        <th className="px-4 py-2.5 font-medium text-gray-500">{t('superadmin.env.table.category')}</th>
                        <th className="px-4 py-2.5 font-medium text-gray-500">{t('superadmin.env.table.status')}</th>
                        <th className="px-4 py-2.5 font-medium text-gray-500">{t('superadmin.env.table.preview')}</th>
                        <th className="px-4 py-2.5 font-medium text-gray-500">{t('superadmin.env.table.actions')}</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {items.map((variable) => (
                        <tr key={variable.key} className="hover:bg-gray-50/70">
                          <td className="px-4 py-2.5 font-medium text-gray-900">{variable.key}</td>
                          <td className="px-4 py-2.5 text-gray-600">{categoryLabels[variable.category] || variable.category}</td>
                          <td className="px-4 py-2.5">
                            <span className={badgeByStatus(variable.has_value)}>
                              {variable.has_value ? (
                                <>
                                  <CheckCircle2 className="h-3.5 w-3.5 mr-1" />
                                  {t('superadmin.env.status.configured')}
                                </>
                              ) : (
                                <>
                                  <AlertCircle className="h-3.5 w-3.5 mr-1" />
                                  {t('superadmin.env.status.missing')}
                                </>
                              )}
                            </span>
                          </td>
                          <td className="px-4 py-2.5 text-gray-500 max-w-xs truncate">
                            {variable.value_preview || '—'}
                          </td>
                          <td className="px-4 py-2.5">
                            <div className="flex items-center gap-2">
                              <button
                                type="button"
                                onClick={() => openEditModal(variable)}
                                className="p-1.5 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded"
                                aria-label={t('common.edit')}
                              >
                                <Edit className="h-4 w-4" />
                              </button>
                              <button
                                type="button"
                                onClick={() => handleDelete(variable)}
                                className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded"
                                aria-label={t('common.delete')}
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {renderModal()}
      <ConfirmationModal {...confirmProps} />
    </div>
  );
};

export default SuperAdminEnvPage;
